import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, getParticipantId } from '../client'
import { submitAction } from '../actions'
import type { ActionRequest, CartView } from '../types'

export const CART_QUERY_KEY = ['cart']

export function useCart(enabled = true) {
  return useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: () => apiFetch<CartView>('api/v1/cart'),
    enabled,
  })
}

// Cart mutations reserve inventory, so they go through the Action framework
// (AR1/AR7) — not plain REST. Each call generates a fresh action_id; absolute
// quantity semantics make a retry converge.
function cartAction(
  actionType: string,
  targetType: 'item' | 'cart',
  targetId: string,
  payload: Record<string, unknown>,
): ActionRequest {
  return {
    action_id: crypto.randomUUID(),
    action_type: actionType,
    vertical: 'farm_to_table',
    actor_id: getParticipantId() ?? '',
    actor_role: 'consumer',
    target_entity_type: targetType,
    target_entity_id: targetId,
    payload,
  }
}

export function useAddToCart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { itemId: string; quantity: number }) =>
      submitAction(
        cartAction('add_to_cart', 'item', params.itemId, {
          item_id: params.itemId,
          quantity: params.quantity,
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY }),
  })
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { itemId: string; quantity: number }) =>
      submitAction(
        cartAction('update_cart_item', 'item', params.itemId, { quantity: params.quantity }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY }),
  })
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) =>
      submitAction(cartAction('remove_cart_item', 'item', itemId, {})),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY }),
  })
}

export function useClearCart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => submitAction(cartAction('clear_cart', 'cart', getParticipantId() ?? '', {})),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY }),
  })
}

// Re-reserve all lines after the cart TTL lapsed (the "Re-reserve all" button).
export function useReserveCart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      submitAction(cartAction('reserve_cart', 'cart', getParticipantId() ?? '', {})),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY }),
  })
}
